import { useEffect, useState, useRef, useCallback } from "react";
import { createFileRoute, useNavigate, Link, useLocation } from "@tanstack/react-router";
import { UserRound, ChevronDown } from "lucide-react";
import { HomepagePdfTile } from "../components/shared/ShirePdfDownload";
import { ElectorateMapV2 } from "../components/shared/ElectorateMapV2";
import { GlobalSearch } from "../components/shared/GlobalSearch";
import { LocalityIndex } from "../components/shared/LocalityIndex";
import { DfesWarningsPanel } from "../components/shared/DfesWarningsPanel";

import { MapHoverProvider } from "../components/shared/MapHoverContext";

import { ShirePopup } from "../components/shared/ShirePopup";
import { SHIRES, type ShireId } from "../components/shared/electorate-shires";
import { POPULATION_HEADLINE } from "../data/wb/profile/population-highlights";

const SHIRE_ID_SET = new Set<string>(SHIRES.map((s) => s.id));

interface MapSearch {
  shire?: ShireId;
  locality?: string;
  invalid?: "shire" | "locality";
}

export const Route = createFileRoute("/")({
  validateSearch: (raw: Record<string, unknown>): MapSearch => {
    const rawShire = typeof raw.shire === "string" ? raw.shire : undefined;
    const rawLocality = typeof raw.locality === "string" ? raw.locality : undefined;

    if (rawShire && !SHIRE_ID_SET.has(rawShire)) {
      return { invalid: "shire" };
    }
    if (rawLocality && !rawShire) {
      return { invalid: "locality" };
    }
    return {
      shire: rawShire as ShireId | undefined,
      locality: rawLocality,
    };
  },
  component: ElectoratePage,
  head: () => ({
    meta: [
      { title: "Warren-Blackwood Electorate — Interactive Map" },
      {
        name: "description",
        content:
          "Interactive map of the Warren-Blackwood electorate — six shires, their localities and towns, with shareable deep links.",
      },
      { property: "og:title", content: "Warren-Blackwood Electorate" },
      {
        property: "og:description",
        content: "Interactive map of the Warren-Blackwood electorate.",
      },
    ],
  }),
});

function ComingSoon({ kind }: { kind: "shire" | "locality" }) {
  return (
    <main
      id="main-content"
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "var(--sp-xl) var(--sp-md)",
        textAlign: "center",
      }}
    >
      <h1
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "clamp(1.75rem, 5vw, 2.5rem)",
          color: "var(--green)",
          marginBottom: "var(--sp-md)",
        }}
      >
        {kind === "shire" ? "That shire isn't on the map" : "We couldn't find that locality"}
      </h1>
      <p style={{ color: "var(--ink-mid)", marginBottom: "var(--sp-lg)", lineHeight: 1.5 }}>
        {kind === "shire"
          ? "Warren-Blackwood covers six shires. The link you followed mentioned a different one."
          : "Locality links need the shire too. The link you followed was missing it, or the locality name didn't match."}
      </p>
      <div
        style={{
          display: "flex",
          gap: "var(--sp-sm)",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Link
          to="/"
          search={{}}
          style={{
            display: "inline-block",
            padding: "0.7rem 1.3rem",
            borderRadius: 999,
            background: "var(--rivergum)",
            color: "white",
            textDecoration: "none",
            fontFamily: "var(--font-sub)",
            fontWeight: 700,
            minHeight: 44,
          }}
        >
          Back to the map
        </Link>
        {kind === "locality" && (
          <Link
            to="/"
            search={{}}
            hash="locality-index-heading"
            style={{
              display: "inline-block",
              padding: "0.7rem 1.3rem",
              borderRadius: 999,
              background: "var(--warm-white)",
              color: "var(--rivergum)",
              textDecoration: "none",
              fontFamily: "var(--font-sub)",
              fontWeight: 700,
              minHeight: 44,
              border: "1px solid var(--rivergum)",
            }}
          >
            Browse all localities
          </Link>
        )}
      </div>
    </main>
  );
}

type TaskSub =
  | { sub: "council"; hash: "rates" | "report" | "waste" | "planning" }
  | { sub: "seniors" };

type PendingTask =
  | {
      mode: "task";
      label: string;
      build: (id: ShireId) => {
        to: "/shire/$name/council" | "/shire/$name/seniors";
        hash?: string;
      };
    }
  | { mode: "change"; label: string };

function ElectoratePage() {
  const { shire, locality, invalid } = Route.useSearch();
  const navigate = useNavigate({ from: "/" });
  const [mounted, setMounted] = useState(false);
  const [storedShire, setStoredShire] = useState<ShireId | null>(null);
  const [pendingTask, setPendingTask] = useState<PendingTask | null>(null);
  const [saveAsMyShire, setSaveAsMyShire] = useState(false);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const firstPickerBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setMounted(true);
    const read = () => {
      try {
        const v = window.localStorage.getItem("wb:my-shire");
        setStoredShire(v && SHIRES.some((s) => s.id === v) ? (v as ShireId) : null);
      } catch {
        /* ignore */
      }
    };
    read();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "wb:my-shire") read();
    };
    const onCustom = () => read();
    window.addEventListener("storage", onStorage);
    window.addEventListener("wb:my-shire-change", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("wb:my-shire-change", onCustom);
    };
  }, []);

  // Picker dialog: ESC to close + initial focus + restore focus on close.
  useEffect(() => {
    if (!pendingTask) return;
    // default save state per open: in 'change' mode the whole point is to
    // change the saved shire, so default ON; in 'task' mode default OFF so
    // we never quietly write to storage.
    setSaveAsMyShire(pendingTask.mode === "change");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePicker();
      }
    };
    window.addEventListener("keydown", onKey);
    // focus first option after paint
    const t = window.setTimeout(() => firstPickerBtnRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTask]);

  const closePicker = useCallback(() => {
    setPendingTask(null);
    // restore focus
    const el = lastFocusedRef.current;
    window.setTimeout(() => el?.focus?.(), 0);
  }, []);

  const openPicker = useCallback((task: PendingTask, trigger: HTMLElement) => {
    lastFocusedRef.current = trigger;
    setPendingTask(task);
  }, []);

  if (invalid) {
    return <ComingSoon kind={invalid} />;
  }

  const setShire = (id: ShireId | null) =>
    navigate({
      search: () => ({ shire: id ?? undefined, locality: undefined }),
      replace: false,
    });

  const setLocality = (loc: string | null) =>
    navigate({
      search: (prev: { shire?: ShireId; locality?: string }) => ({
        ...prev,
        locality: loc ?? undefined,
      }),
      replace: true,
    });

  const location = useLocation();
  const isProfileActive =
    location.pathname === "/profile" || location.pathname.startsWith("/profile/");

  // Precedence: URL shire wins over stored. If the user actively tapped a
  // shire on the map, the Common Tasks strip reflects THAT shire — never
  // contradicts what's visibly selected. The "use my saved shire" affordance
  // below swaps context back when there's a conflict, without losing the
  // saved value.
  const effectiveShire: ShireId | undefined = shire ?? storedShire ?? undefined;
  const effectiveShireName = effectiveShire
    ? (SHIRES.find((s) => s.id === effectiveShire)?.name ?? null)
    : null;
  const showConflictNote = !!shire && !!storedShire && shire !== storedShire;
  const storedShireName = storedShire
    ? (SHIRES.find((s) => s.id === storedShire)?.name ?? null)
    : null;

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "var(--sp-md) var(--sp-md) var(--sp-xl)",
      }}
    >
      <GlobalSearch />
      <div style={{ marginBottom: "var(--sp-md)" }}>
        <DfesWarningsPanel scopeLabel="Warren-Blackwood" />
      </div>
      <header style={{ marginBottom: "var(--sp-md)" }}>
        <p
          style={{
            fontFamily: "var(--font-sub)",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontSize: "0.75rem",
            color: "var(--rivergum)",
            margin: 0,
            marginBottom: "0.35rem",
          }}
        >
          WA State Electorate
        </p>
        <h1
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "clamp(2rem, 5.5vw, 3rem)",
            color: "var(--rivergum)",
            lineHeight: 1.05,
            margin: 0,
          }}
        >
          Warren-Blackwood
        </h1>
        <p
          style={{
            color: "var(--off-black)",
            marginTop: "0.6rem",
            fontSize: "1.05rem",
            maxWidth: "60ch",
            lineHeight: 1.45,
          }}
        >
          <strong>{POPULATION_HEADLINE.erp2025.toLocaleString()} residents</strong> across{" "}
          <strong>{POPULATION_HEADLINE.landAreaKm2.toLocaleString()} km²</strong> of WA's south-west
          — {SHIRES.length} shires, from the Margaret River coast to Denmark.
        </p>
        <p
          style={{
            marginTop: "0.75rem",
            fontSize: "0.95rem",
            color: "var(--ink-mid)",
          }}
        >
          Tap a shire on the map to see its summary and localities.
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-sm)",
            alignItems: "center",
            marginTop: "var(--sp-sm)",
          }}
        >
          <Link
            to="/profile"
            className="community-profile-btn"
            aria-current={isProfileActive ? "page" : undefined}
            aria-label={
              isProfileActive
                ? "Community profile (current page)"
                : "Open the Warren-Blackwood community profile"
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.55rem 1.1rem",
              borderRadius: 999,
              background: isProfileActive ? "var(--dusty-sage)" : "var(--rivergum)",
              color: isProfileActive ? "var(--rivergum)" : "var(--warm-white)",
              textDecoration: "none",
              fontFamily: "var(--font-sub)",
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: "0.02em",
              boxShadow: isProfileActive ? "inset 0 0 0 2px var(--rivergum)" : "none",
              minHeight: 44,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <UserRound size={16} aria-hidden="true" focusable="false" />
            <span>Community profile</span>
          </Link>
          <HomepagePdfTile shire={shire} />
        </div>
      </header>

      <MapHoverProvider>
        <div className="wb-map-list">
          <div className="wb-map-list__map">
            <ElectorateMapV2
              shireId={shire ?? null}
              locality={locality ?? null}
              onShireChange={setShire}
              onLocalityChange={setLocality}
            />
          </div>
          <LocalityIndex activeShireId={shire ?? null} onSelectShire={(id) => setShire(id)} />
        </div>

        <section aria-labelledby="common-tasks-heading" style={{ marginTop: "var(--sp-xl)" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: "0.5rem 0.85rem",
              marginBottom: "var(--sp-sm)",
            }}
          >
            <h2
              id="common-tasks-heading"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(1.4rem, 3vw, 1.85rem)",
                color: "var(--rivergum)",
                margin: 0,
              }}
            >
              {effectiveShireName ? `Common tasks — ${effectiveShireName}` : "Common tasks"}
            </h2>
            {effectiveShireName && (
              <button
                type="button"
                onClick={(e) =>
                  openPicker({ mode: "change", label: "Change your shire" }, e.currentTarget)
                }
                aria-haspopup="dialog"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "var(--rivergum)",
                  fontFamily: "var(--font-sub)",
                  fontSize: "0.85rem",
                  textDecoration: "underline",
                  cursor: "pointer",
                  minHeight: 32,
                }}
              >
                Not {effectiveShireName}?
              </button>
            )}
          </div>

          {showConflictNote && storedShireName && (
            <p
              role="status"
              style={{
                margin: "0 0 var(--sp-sm)",
                fontSize: "0.85rem",
                color: "var(--ink-mid)",
                lineHeight: 1.4,
              }}
            >
              Showing tasks for <strong>{effectiveShireName}</strong> (selected on the map).{" "}
              <Link
                to="/"
                search={{}}
                style={{ color: "var(--rivergum)", textDecoration: "underline" }}
              >
                Use my saved shire ({storedShireName})
              </Link>
              .
            </p>
          )}

          <div className="common-tasks-grid">
            {(() => {
              type Task =
                | ({ label: string; kind: "shire"; eyebrow?: string; span?: boolean } & TaskSub)
                | { label: string; kind: "contact" }
                | { label: string; kind: "internal"; to: "/civic/enrol" };
              const tasks: Task[] = [
                { label: "Pay my rates", kind: "shire", sub: "council", hash: "rates" },
                {
                  label: "Report a road or local issue",
                  kind: "shire",
                  sub: "council",
                  hash: "report",
                },
                { label: "Find my bin day", kind: "shire", sub: "council", hash: "waste" },
                { label: "Building & planning", kind: "shire", sub: "council", hash: "planning" },
                { label: "Contact Bevan", kind: "contact" },
                { label: "Enrol to vote", kind: "internal", to: "/civic/enrol" },
                {
                  label: "Seniors services",
                  kind: "shire",
                  sub: "seniors",
                  span: true,
                  eyebrow: "For older residents and carers",
                },
              ];
              // Contact carries through the active shire (URL > stored), so
              // tapping a shire on the map then "Contact Bevan" lets Bevan's
              // office see which shire the message came from.
              const contactFrom: ShireId | undefined = shire ?? storedShire ?? undefined;

              const sharedStyle = {
                display: "flex",
                flexDirection: "column" as const,
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center" as const,
                padding: "0.85rem 1rem",
                border: "1px solid var(--rule)",
                borderRadius: 6,
                background: "var(--warm-white)",
                color: "var(--rivergum)",
                fontFamily: "var(--font-sub)",
                fontWeight: 700,
                fontSize: "0.9rem",
                minHeight: 44,
                textDecoration: "none",
                cursor: "pointer",
                gap: "0.2rem",
              };

              return tasks.map((task) => {
                const tileClass =
                  "span" in task && task.span
                    ? "common-task-tile common-task-tile--span"
                    : "common-task-tile";

                if (task.kind === "internal") {
                  return (
                    <Link key={task.label} to={task.to} className={tileClass} style={sharedStyle}>
                      <span>{task.label}</span>
                    </Link>
                  );
                }

                if (task.kind === "contact") {
                  return (
                    <Link
                      key={task.label}
                      to="/contact"
                      search={contactFrom ? { from: contactFrom } : {}}
                      className={tileClass}
                      style={sharedStyle}
                    >
                      <span>{task.label}</span>
                    </Link>
                  );
                }

                if (effectiveShire) {
                  const labelLine = (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                      {task.span && <UserRound size={18} aria-hidden="true" />}
                      <span>{task.label}</span>
                    </span>
                  );
                  if (task.sub === "council") {
                    return (
                      <Link
                        key={task.label}
                        to="/shire/$name/council"
                        params={{ name: effectiveShire }}
                        hash={task.hash}
                        className={tileClass}
                        style={sharedStyle}
                        aria-label={
                          effectiveShireName ? `${task.label} — ${effectiveShireName}` : task.label
                        }
                      >
                        {labelLine}
                        {task.eyebrow && (
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              color: "var(--ink-mid)",
                            }}
                          >
                            {task.eyebrow}
                          </span>
                        )}
                      </Link>
                    );
                  }
                  return (
                    <Link
                      key={task.label}
                      to="/shire/$name/seniors"
                      params={{ name: effectiveShire }}
                      className={tileClass}
                      style={sharedStyle}
                      aria-label={
                        effectiveShireName ? `${task.label} — ${effectiveShireName}` : task.label
                      }
                    >
                      {labelLine}
                      {task.eyebrow && (
                        <span
                          style={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            color: "var(--ink-mid)",
                          }}
                        >
                          {task.eyebrow}
                        </span>
                      )}
                    </Link>
                  );
                }

                // No shire context: show a visible "choose your shire" hint
                // so the tile doesn't open a surprise modal.
                const build = (id: ShireId) =>
                  task.sub === "council"
                    ? { to: "/shire/$name/council" as const, hash: task.hash }
                    : { to: "/shire/$name/seniors" as const };
                return (
                  <button
                    key={task.label}
                    type="button"
                    onClick={(e) =>
                      openPicker({ mode: "task", label: task.label, build }, e.currentTarget)
                    }
                    aria-label={`${task.label} — choose your shire`}
                    aria-haspopup="dialog"
                    className={tileClass}
                    style={sharedStyle}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                      {task.span && <UserRound size={18} aria-hidden="true" />}
                      <span>{task.label}</span>
                    </span>
                    {task.eyebrow && (
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          color: "var(--ink-mid)",
                        }}
                      >
                        {task.eyebrow}
                      </span>
                    )}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        fontWeight: 500,
                        fontSize: "0.72rem",
                        color: "var(--ink-mid)",
                      }}
                    >
                      Choose your shire
                      <ChevronDown size={12} aria-hidden="true" />
                    </span>
                  </button>
                );
              });
            })()}
          </div>
        </section>
      </MapHoverProvider>

      {mounted && (
        <ShirePopup
          shireId={shire ?? null}
          locality={locality ?? null}
          onShireChange={setShire}
          onLocalityChange={setLocality}
        />
      )}

      {pendingTask && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="picker-title"
          aria-describedby="picker-desc"
          onClick={() => closePicker()}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--warm-white)",
              borderRadius: 10,
              maxWidth: 440,
              width: "100%",
              padding: "1.1rem 1.1rem 0.95rem",
              boxShadow: "0 18px 48px rgba(0,0,0,0.25)",
              border: "1px solid var(--rule)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "0.5rem",
              }}
            >
              <h2
                id="picker-title"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--rivergum)",
                  fontSize: "1.15rem",
                  margin: "0 0 0.25rem",
                }}
              >
                {pendingTask.mode === "change" ? "Change your shire" : pendingTask.label}
              </h2>
              <button
                type="button"
                onClick={() => closePicker()}
                aria-label="Close"
                style={{
                  background: "none",
                  border: "1px solid var(--rule)",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  fontSize: "1.2rem",
                  lineHeight: 1,
                  color: "var(--rivergum)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
            <p
              id="picker-desc"
              style={{
                fontFamily: "var(--font-sub)",
                fontSize: "0.9rem",
                color: "var(--rivergum)",
                margin: "0 0 0.6rem",
                lineHeight: 1.4,
              }}
            >
              {pendingTask.mode === "change"
                ? "Pick the shire you'd like to use. We'll update your saved shire."
                : `Which shire are you in? We won't remember this unless you tick the box below.`}
            </p>

            {pendingTask.mode === "task" && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontFamily: "var(--font-sub)",
                  fontSize: "0.85rem",
                  color: "var(--rivergum)",
                  padding: "0.45rem 0",
                  margin: "0 0 0.4rem",
                  cursor: "pointer",
                  minHeight: 44,
                }}
              >
                <input
                  type="checkbox"
                  checked={saveAsMyShire}
                  onChange={(e) => setSaveAsMyShire(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                Save as my shire (you can change this anytime)
              </label>
            )}

            <div style={{ display: "grid", gap: "0.4rem" }}>
              {SHIRES.map((s, i) => (
                <button
                  key={s.id}
                  ref={i === 0 ? firstPickerBtnRef : undefined}
                  type="button"
                  onClick={() => {
                    const task = pendingTask;
                    if (!task) return;
                    const shouldSave = task.mode === "change" ? true : saveAsMyShire;
                    if (shouldSave) {
                      try {
                        window.localStorage.setItem("wb:my-shire", s.id);
                        window.dispatchEvent(
                          new CustomEvent("wb:my-shire-change", { detail: s.id }),
                        );
                      } catch {
                        /* ignore */
                      }
                      setStoredShire(s.id);
                    }

                    if (task.mode === "task") {
                      const target = task.build(s.id);
                      setPendingTask(null);
                      if (target.to === "/shire/$name/council") {
                        navigate({
                          to: "/shire/$name/council",
                          params: { name: s.id },
                          hash: target.hash,
                        });
                      } else {
                        navigate({
                          to: "/shire/$name/seniors",
                          params: { name: s.id },
                        });
                      }
                    } else {
                      // change mode: stay on homepage
                      closePicker();
                    }
                  }}
                  style={{
                    textAlign: "left",
                    padding: "0.75rem 0.9rem",
                    minHeight: 44,
                    border: "1px solid var(--rule)",
                    borderRadius: 6,
                    background: "var(--warm-white)",
                    color: "var(--rivergum)",
                    fontFamily: "var(--font-sub)",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                  }}
                >
                  {pendingTask.mode === "task" ? `Use ${s.name} for this task` : s.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => closePicker()}
              style={{
                marginTop: "0.85rem",
                background: "none",
                border: "none",
                padding: "0.4rem 0",
                color: "var(--rivergum)",
                fontFamily: "var(--font-sub)",
                fontSize: "0.85rem",
                textDecoration: "underline",
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
